import "@tento-ui/components/codemirror";
import "@tento-ui/components/icon-button";

import { LonaWebComponent, html } from "@tento-ui/component";
import { component } from "@tento-ui/component-decorators";
import { css, Style } from "@tento-ui/component-styles";
import { DomUtils } from "@tento-ui/dom";
import { CodeMirrorEditor } from "@tento-ui/components/codemirror";
import { IconButton } from "@tento-ui/components/icon-button";
import { GESTURE_MANAGER } from "@tento-ui/gesture-manager";
import { Lisp } from "@tento-lisp/lisp";
import { DateTime, NaiveDate, Utc } from "@tento-chrono";
import {
  Appearance,
  Formula,
  SheetLispEnvironment,
  LonaSdk,
  SheetHandle,
  RequestErrorHandlers,
  DebounceExecutors,
  SheetHost,
  MemoryStorage,
} from "@tento-lona";
import { platformOf } from "@tento-lona/host";
import { LispExample } from "@tento-ui/components/lisp-example/lisp-example";
import {
  InvalidationSink,
  type Option,
  SheetNetworkKey,
  SheetRegistries,
  type Timescale,
  UserId,
} from "@tento-lona/sheets";

import PlayFillIcon from "@tento-ui/icons/bootstrap/play-fill.svg";

@component({
  name: "std-sheet-lisp-example",
})
export class SheetLispExample extends LonaWebComponent {
  #$editor: CodeMirrorEditor = this.hydrate("editor");
  #$runButton: IconButton = this.hydrate("run-button");
  #$output = this.$("output");
  #$styleIndicators = this.$("style-indicators");

  #sdk = new LonaSdk.Client({
    storage: new MemoryStorage(),
    host: SheetHost.HEADLESS,
    registries: SheetRegistries.BUILTINS,
    invalidation: InvalidationSink.create(),
    onSheetPromoted: null,
    baseUrl: null,
    accessToken: null,
    fetch: null,
    rowsOptions: (sdk) => ({
      backend: sdk.httpBackend(),
      calendarConfig: LonaSdk.Client.EMPTY_CALENDAR_CONFIG,
      taskListConfig: LonaSdk.Client.EMPTY_TASK_LIST_CONFIG,
      calendarPrefs: null,
    }),
    requestErrorHandler: RequestErrorHandlers.TRACE,
    debounceExecutor: DebounceExecutors.inProcess(),
    appearance: Appearance.HEADLESS,
  });
  #handle: Option<SheetHandle> = null;
  #tree: Option<ReturnType<SheetHandle["joinedTree"]>> = null;
  #currentColumn: NaiveDate.Partial = NaiveDate.Partial.fromDateAndType(
    "week",
    NaiveDate.today(),
  );

  constructor() {
    super();
    this.#$runButton.bindIcon(PlayFillIcon, { squarish: true });
  }

  async bind(props: SheetLispExample.Props = {}): Promise<this> {
    let timeScale = props.timescale ?? "week";
    let rows: LonaSdk.EphemeralRow[] = props.rows ?? [];
    let columnStr: Option<string> = null;

    if (props.configJson) {
      try {
        const config = JSON.parse(props.configJson) as SheetLispExample.Config;
        timeScale = config.timescale ?? timeScale;
        rows = config.rows ?? rows;
        columnStr = config.column ?? null;
      } catch {}
    }

    const now = DateTime.utc.now();
    // Must be a parseable SheetId; the legacy `ephemeral(<name>)` form interns
    // to a deterministic UUID. A bare name like "demo-sheet" is rejected by
    // SheetId.of ("Invalid SheetId format"), which broke every sheet example.
    this.#handle = await this.#sdk.openEphemeral(
      {
        id: SheetNetworkKey.fromWire("ephemeral(demo-sheet)"),
        creatorId: UserId.of("test"),
        role: "owner",
        timeScale,
        rows,
        lookupKey: null,
        title: null,
        description: null,
        width: null,
      },
      { onExisting: "reuse", mode: "ephemeral", factory: null },
    );

    this.#tree = this.#handle.joinedTree({
      timescale: timeScale,
      host: SheetHost.HEADLESS,
      invalidation: InvalidationSink.NOOP,
    });

    if (props.column) {
      this.#currentColumn = props.column;
    } else if (columnStr) {
      const parseResult = NaiveDate.Partial.parse(columnStr);
      const parsed = parseResult.asOk();
      if (parsed) {
        this.#currentColumn = parsed;
      } else {
        this.#currentColumn = NaiveDate.Partial.fromDateAndType(
          timeScale,
          NaiveDate.today(),
        );
      }
    } else {
      this.#currentColumn = NaiveDate.Partial.fromDateAndType(
        timeScale,
        NaiveDate.today(),
      );
    }

    await this.#$editor.bind({
      content: props.code ?? "",
      language: "lisp",
      editable: props.editable ?? true,
      onUpdate: () => {
        this.#$output.textContent = "";
      },
    });

    GESTURE_MANAGER.addPointerEvent(this.#$runButton, {
      onClick: () => this.#runCode(),
    });

    return this;
  }

  #runCode(): void {
    if (!this.#tree || !this.#handle) {
      this.#$output.textContent = "Error: Sheet not initialized";
      this.#$output.style.color = "var(--red)";
      return;
    }

    const code = this.#$editor.value;
    if (!code) {
      this.#$output.textContent = "";
      return;
    }

    const row = this.#tree.flattened.rows[0];
    if (!row) {
      this.#$output.textContent = "Error: No rows in sheet";
      this.#$output.style.color = "var(--red)";
      return;
    }

    const style = new Map<string, string>();
    const env = new SheetLispEnvironment(new Map(), {
      tree: this.#tree,
      sheet: Formula.makeSheetReads(this.#tree, platformOf(this.#handle)),
      row,
      column: this.#currentColumn,
      style,
      tz: null,
      dbg: null,
    });

    const expression = Lisp.parseAll(code);
    const result = Formula.run(expression, code, env);
    this.#$output.textContent = LispExample.display(result);
    this.#$output.style.color = "var(--text-sec)";

    DomUtils.clearChildren(this.#$styleIndicators);
    for (const [key, value] of style) {
      const $indicator = document.createElement("div");
      $indicator.style.width = "20px";
      $indicator.style.height = "20px";
      $indicator.style.backgroundColor = `var(--${value})`;
      $indicator.style.borderRadius = "4px";
      $indicator.style.border = "1px solid var(--divider)";
      $indicator.style.flexShrink = "0";
      $indicator.title = `${key}: ${value}`;
      this.#$styleIndicators.appendChild($indicator);
    }
  }

  static $styles: Style.Compat[] = [
    LispExample.$style,
    css`
      #style-indicators {
        gap: 4px;
        align-items: center;
        margin-left: 8px;
      }
    `,
  ];
  static $html: HTMLTemplateElement = html`
    <std-col id=root>
      <std-codemirror id="editor"></std-codemirror>
      <std-row id="controls">
        <std-icon-button id="run-button" squarish></std-icon-button>
        <p id="output" class="small"></p>
        <std-row id="style-indicators"></std-row>
      </std-row>
    </std-col>
  `;
}

export namespace SheetLispExample {
  export type Props = {
    code?: string;
    editable?: boolean;
    rows?: LonaSdk.EphemeralRow[];
    timescale?: Timescale;
    column?: NaiveDate.Partial;
    configJson?: string;
  };

  export type Config = {
    column?: string;
    rows?: LonaSdk.EphemeralRow[];
    timescale?: Timescale;
  };
}
